import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';
import { AuditService } from '../../services/audit.service';
import { SuspiciousActivityService } from '../../services/suspicious-activity.service';
import { DonationService } from '../../services/donation.service';
import { ExpenseService } from '../../services/expense.service';
import { AuditLogApiActions, AuditLogPageActions, ExpenseAuditApiActions, ExpenseAuditPageActions, FinancialAuditApiActions, FinancialAuditPageActions, SecurityAuditApiActions, SecurityAuditPageActions, SuspiciousActivityApiActions, SuspiciousActivityPageActions } from './audit.actions';

@Injectable()
export class AuditEffects {
  private readonly actions$ = inject(Actions);
  private readonly auditService = inject(AuditService);
  private readonly suspiciousActivityService = inject(SuspiciousActivityService);
  private readonly donationService = inject(DonationService);
  private readonly expenseService = inject(ExpenseService);

  readonly loadAuditLogs$ = createEffect(() => this.actions$.pipe(
    ofType(AuditLogPageActions.opened, AuditLogPageActions.filtersChanged, AuditLogPageActions.pageChanged),
    switchMap(({ filters }) => this.auditService.getLogs(filters).pipe(
      map((response) => AuditLogApiActions.loadSucceeded({ logs: response.data, total: response.total })),
      catchError((error: { message?: string }) => of(AuditLogApiActions.loadFailed({ error: error?.message ?? 'Unable to load audit logs' }))),
    )),
  ));

  readonly loadSuspiciousActivity$ = createEffect(() => this.actions$.pipe(
    ofType(SuspiciousActivityPageActions.opened, SuspiciousActivityPageActions.filtersChanged, SuspiciousActivityPageActions.pageChanged),
    switchMap(({ filters }) => this.suspiciousActivityService.getAlerts(filters).pipe(
      map((response) => SuspiciousActivityApiActions.loadSucceeded({ alerts: response.data, total: response.total, pendingCount: response.pendingCount })),
      catchError((error: { message?: string }) => of(SuspiciousActivityApiActions.loadFailed({ error: error?.message ?? 'Unable to load suspicious activity' }))),
    )),
  ));

  readonly loadFinancialAudit$ = createEffect(() => this.actions$.pipe(
    ofType(FinancialAuditPageActions.opened, FinancialAuditPageActions.filtersChanged, FinancialAuditPageActions.pageChanged),
    switchMap(({ filters }) => this.donationService.getFinancialAudit(filters).pipe(
      map((response) => FinancialAuditApiActions.loadSucceeded({ logs: response.items, total: response.total })),
      catchError((error: { message?: string }) => of(FinancialAuditApiActions.loadFailed({ error: error?.message ?? 'Unable to load financial audit' }))),
    )),
  ));

  readonly loadExpenseAudit$ = createEffect(() => this.actions$.pipe(
    ofType(ExpenseAuditPageActions.opened, ExpenseAuditPageActions.filtersChanged, ExpenseAuditPageActions.pageChanged),
    switchMap(({ filters }) => this.expenseService.getAudit(filters).pipe(
      map((response) => ExpenseAuditApiActions.loadSucceeded({ logs: response.items, total: response.total })),
      catchError((error: { message?: string }) => of(ExpenseAuditApiActions.loadFailed({ error: error?.message ?? 'Unable to load expense audit' }))),
    )),
  ));

  readonly loadSecurityAudit$ = createEffect(() => this.actions$.pipe(
    ofType(SecurityAuditPageActions.opened, SecurityAuditPageActions.filtersChanged, SecurityAuditPageActions.pageChanged),
    switchMap(({ filters }) => this.auditService.getSecurityLogs(filters).pipe(
      map((response) => SecurityAuditApiActions.loadSucceeded({ logs: response.data, total: response.total })),
      catchError((error: { message?: string }) => of(SecurityAuditApiActions.loadFailed({ error: error?.message ?? 'Unable to load security audit' }))),
    )),
  ));
}